// Kadro talebi aksiyon görünürlüğü — backend'in yetki matrisinin FE karşılığı
// (AtsRequisitionDataAccess.ChangeStatus / Update / Delete):
//   - AT.Write / admin (writable): tüm geçişler + düzenle + sil.
//   - Satırın HM sahibi (AT.Read): gönder + taslağa çek + ONAY ÖNCESİ iptal + düzenle.
//   - Satırın atanmış HR BP'si (AT.Read): onayla + reddet + taslağa çek + ONAY ÖNCESİ iptal.
//   - Diğerleri: hiçbir aksiyon (satırı zaten yalnız kapsamındakiler görür).
// ⚠ BP-31: onay SONRASI iptal (approved / on_hold / posting_opened) artık AT.Write ister —
// kaskad ilanları arşivler, açık başvuruları eler ve GÖNDERİLMİŞ teklifleri geri çeker.
// Backend aynı satırda 403 `ats.requisition.cancelRequiresWriteAfterApproval` döner;
// buradaki süzgeç, arayüzün backend'in kabul etmeyeceği bir düğme çizmemesi içindir.
// Kimlik eşleşmesi backend ResolveOwnerIds ile aynı çift-anahtar kuralını izler:
// atama alanları login User.Id VEYA Employee.Id taşıyabilir.
// Geçiş tablosu backend ResolveTransition'ın birebir karşılığıdır (ATS Faz 1 FE-32):
// pending_approval'dan iptal, on_hold'dan tamamlama ve `posting_opened` (ilanı açılmış talep:
// yalnız fill | cancel) dahil.

// Onaylarım panelinin derin bağlantı kipi (/ats/requisitions?mode=...).
// SAF modülde durur ki hem sayfa hem panel kayıt defteri (approvals/config/
// categories.js) hem de testler tek kaynaktan okusun; iki dosyada aynı string
// taşınsaydı bir yeniden adlandırmada sessizce ayrışırlardı.
export const PENDING_APPROVAL_MODE = 'pendingApproval';

// "Açık kadro" tanımı — İşe Alım Panosu'nun KPI kartı (AtsDashboardDataAccess
// .OpenRequisitionStatuses) ile AYNI küme. Kart "12 açık kadro" deyip listenin
// başka bir küme göstermesi sessiz bir yalan olurdu; bu yüzden tek sabit.
// Derin bağlantı: /ats/requisitions?status=approved,posting_opened
export const OPEN_REQUISITION_STATUSES = ['approved', 'posting_opened'];

export const ALLOWED_TRANSITIONS = {
  draft: [
    { action: 'submit_for_approval', labelKey: 'ats.requisition.action.submitForApproval' },
    { action: 'cancel', labelKey: 'ats.requisition.action.cancel' },
  ],
  pending_approval: [
    { action: 'approve', labelKey: 'ats.requisition.action.approve' },
    { action: 'reject', labelKey: 'ats.requisition.action.reject', requiresReason: true },
    { action: 'return_to_draft', labelKey: 'ats.requisition.action.returnToDraft' },
    { action: 'cancel', labelKey: 'ats.requisition.action.cancel' },
  ],
  approved: [
    { action: 'hold', labelKey: 'ats.requisition.action.hold' },
    { action: 'fill', labelKey: 'ats.requisition.action.fill' },
    { action: 'cancel', labelKey: 'ats.requisition.action.cancel' },
  ],
  on_hold: [
    { action: 'resume', labelKey: 'ats.requisition.action.resume' },
    { action: 'fill', labelKey: 'ats.requisition.action.fill' },
    { action: 'cancel', labelKey: 'ats.requisition.action.cancel' },
  ],
  posting_opened: [
    // BP-23: ilanı açılmış kadroyu geçici dondurmanın tek yolu yıkıcı iptaldi.
    { action: 'hold', labelKey: 'ats.requisition.action.hold' },
    { action: 'fill', labelKey: 'ats.requisition.action.fill' },
    { action: 'cancel', labelKey: 'ats.requisition.action.cancel' },
  ],
  // BP-23: reddedilen talep çıkışsızdı — düzeltip yeniden onaya göndermenin yolu yoktu.
  rejected: [
    { action: 'return_to_draft', labelKey: 'ats.requisition.action.returnToDraft' },
  ],
  filled: [],
  // BP-31: iptal de çıkışsızdı. Yeniden açma ilanları `paused`a döndürür; kaskadın
  // kapattığı başvuru/teklifler geri GELMEZ (yalnız AT.Write).
  cancelled: [
    { action: 'reopen', labelKey: 'ats.requisition.action.reopen' },
  ],
};

// Backend ChangeStatus'un ayrıcalıksız (non-privileged) izin kümeleri.
const HM_ACTIONS = ['submit_for_approval', 'return_to_draft', 'cancel'];
const HRBP_ACTIONS = ['approve', 'reject', 'return_to_draft', 'cancel'];

// Sahibin (HM / HR BP) iptal edebildiği durumlar — onay ÖNCESİ. Sonrası AT.Write.
export const OWNER_CANCELLABLE_STATUSES = ['draft', 'pending_approval'];

// AuthContext kullanıcısından kimlik kümesi: { login User.Id, Employee.Id? }.
export function ownerIdsOf(user) {
  if (!user) return [];
  return [user.id, user.employeeId].filter(Boolean);
}

const isRowHm = (row, ownerIds) => ownerIds.includes(row.hiringManagerUserId);
const isRowHrBp = (row, ownerIds) => row.hrBpUserId != null && ownerIds.includes(row.hrBpUserId);

export function visibleTransitions(row, { writable, ownerIds }) {
  if (!row) return [];
  const transitions = ALLOWED_TRANSITIONS[row.status] || [];
  if (writable) return transitions;

  const allowed = new Set([
    ...(isRowHm(row, ownerIds) ? HM_ACTIONS : []),
    ...(isRowHrBp(row, ownerIds) ? HRBP_ACTIONS : []),
  ]);
  const ownerMayCancel = OWNER_CANCELLABLE_STATUSES.includes(row.status);
  return transitions.filter((tr) => allowed.has(tr.action)
    && (tr.action !== 'cancel' || ownerMayCancel));
}

// Düzenleme onay öncesiyle sınırlı (draft/pending_approval) — sonrası maaş
// bandı istisna akışına girer. Ayrıcalıksız kullanıcı yalnız kendi (HM) talebini
// düzenler (backend Update sahiplik kapısıyla aynı).
export function canEditRow(row, { writable, ownerIds }) {
  if (!row) return false;
  if (row.status !== 'draft' && row.status !== 'pending_approval') return false;
  return writable || isRowHm(row, ownerIds);
}

// Silme controller'da AT.Write ile korunur.
export const canDeleteRow = ({ writable }) => !!writable;

// ═══════ Kadro ↔ pozisyon modeli (SPEC-kadro-pozisyon) ═══════
// `existing`: mevcut (boş / sahibi ayrılan) tek pozisyon — kişi sayısı SABİT 1.
// `new`: onayda `ApprovedHeadcount ?? RequestedHeadcount` kadar pozisyon açılır.
// `legacy`: modelden önceki satırlar — eski davranış AYNEN (mod seçimi yok).
export const POSITION_MODES = ['existing', 'new'];
export const NEW_MODE_MAX_HEADCOUNT = 200;

// Satırda `positionMode` yoksa (henüz göç etmemiş yanıt) satır legacy sayılır;
// yeni formda (id yok) varsayılan `existing`.
export function positionModeOf(row) {
  if (!row) return 'existing';
  if (row.positionMode) return row.positionMode;
  return row.id ? 'legacy' : 'existing';
}

const num = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : 0);

// Talebin henüz dolmamış (açık) koltuk sayısı. `seatsOpen` yoksa toplam − dolu.
export function openSeatCount(row) {
  if (!row) return 0;
  if (row.seatsOpen != null) return Math.max(0, num(row.seatsOpen));
  return Math.max(0, num(row.seatsTotal) - num(row.seatsFilled));
}

// İptal / erken kapanış diyaloğundaki "boş pozisyonları da kapat" tik kutusu:
// yalnız `new` modda VE talebin dolmamış koltuğu varken. `existing` pozisyona
// backend ASLA dokunmaz; legacy'de talep pozisyon açmadı.
export const CLOSE_UNFILLED_ACTIONS = ['cancel', 'fill'];
export function offersCloseUnfilled(row, action) {
  return CLOSE_UNFILLED_ACTIONS.includes(action)
    && positionModeOf(row) === 'new'
    && openSeatCount(row) > 0;
}

// Onaycının "onaylanan kişi sayısı" girdisi: yalnız `new` modda (existing'de 1 sabit,
// legacy'de eski akış — girdi yoktu).
export function offersApprovedHeadcount(row, action) {
  return action === 'approve' && positionModeOf(row) === 'new';
}

// Liste/detay "Doluluk" hücresinin verisi. Koltuk seti varsa `{ filled, total }`;
// yoksa (legacy ya da onaylanmamış `new`) eski kişi sayısı gösterimi `{ headcount }`.
export function seatProgressOf(row) {
  if (!row) return { headcount: null };
  const mode = positionModeOf(row);
  const total = num(row.seatsTotal);
  if (mode !== 'legacy' && total > 0) {
    return { filled: num(row.seatsFilled), total };
  }
  return { headcount: row.requestedHeadcount ?? null };
}

// "[KOD] Unvan" — liste kolonu, detay çekmecesi ve onay diyaloğu AYNI metni kullanır.
// `new` modda talep açılırken pozisyon YOKTUR: kod boş, unvan (varsa) talebin ünvanı.
export function requisitionPositionText(row) {
  if (!row) return '-';
  const code = row.positionCode ? `[${row.positionCode}] ` : '';
  const title = row.positionTitleName || row.titleName || '';
  return (code + title).trim() || '-';
}

// "3/20 Dolu" ya da (legacy / pozisyonu henüz açılmamış talep) kişi sayısı.
export function seatProgressText(row, t) {
  const p = seatProgressOf(row);
  if (p.total != null) return t('ats.requisition.seatProgress', { filled: p.filled, total: p.total });
  return p.headcount == null ? '-' : String(p.headcount);
}
